import React, { createContext, useContext, useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut as amplifySignOut, getCurrentUser, fetchAuthSession } from '@aws-amplify/auth';
import { appConfig } from '../config/appConfig';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: appConfig.aws.userPoolId,
      userPoolClientId: appConfig.aws.clientId,
      identityPoolId: appConfig.aws.identityPoolId,
      loginWith: {
        oauth: {
          domain: appConfig.aws.cognitoDomain.replace('https://', '').replace(/\/$/, ''),
          scopes: ['openid', 'email'],
          redirectSignIn: [window.location.origin],
          redirectSignOut: [window.location.origin],
          responseType: 'code',
        },
      },
    },
  },
});

interface AuthContextType {
  user: any;
  signIn: () => void;
  signOut: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Check if we're returning from OAuth redirect
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthCode = urlParams.has('code');
      
      if (hasAuthCode) {
        // Give Amplify time to complete the OAuth flow
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      await checkUser();
    };
    
    initAuth();
    
    // Listen for auth state changes after OAuth redirect
    const handleAuthChange = () => {
      checkUser();
    };
    
    window.addEventListener('focus', handleAuthChange);
    return () => window.removeEventListener('focus', handleAuthChange);
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      if (session.tokens) {
        // Get email from ID token
        const attributes = session.tokens?.idToken?.payload;
        const email = attributes?.email || currentUser.username;
        setUser({ ...currentUser, name: email });
      }
    } catch (error) {
      setUser(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async () => {
    await signInWithRedirect();
  };

  const signOut = async () => {
    setLoading(true);
    await amplifySignOut();
    setUser(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="text-gray-900 dark:text-white">
        {user ? 'Signing out...' : 'Loading...'}
      </div>
    </div>;
  }

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, isAuthenticated: !!user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
